import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { fmtHM } from './ReportsPanel';

/* ---- redesigned timesheet helpers ------------------------------------- */
const NUMS = { fontVariantNumeric: 'tabular-nums' };
const attLbl = { fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#8a949c', marginBottom: 3 };
const attMenuItem = (color) => ({
  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
  border: 'none', background: 'none', borderRadius: 7,
  fontSize: 12.5, fontWeight: 700, color, cursor: 'pointer',
});
const ENGAGEMENT = {
  hourly: { label: 'Hourly', bg: '#ede9fe', fg: '#6d28d9' },
  office: { label: 'Office', bg: '#d1fadf', fg: '#039855' },
};
const initialsOf = (n) => String(n || '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

// Suburb out of a full address, so the table shows a chip instead of three
// wrapped lines. Australian addresses read "12 Smith St, O'Connor WA 6163".
function suburbOf(addr) {
  if (!addr) return null;
  const parts = String(addr).split(',').map(x => x.trim()).filter(Boolean);
  if (!parts.length) return null;
  const tail = parts.length > 1 ? parts[parts.length - 1] : parts[0];
  return tail.replace(/(WA|NSW|VIC|QLD|SA|NT|TAS|ACT)/g, '').replace(/\d{4}/g, '').replace(/australia/i, '').replace(/\s+/g, ' ').trim() || parts[0];
}

// Coverage bands — how much of their own cost the person produced.
const coverBand = (pct) => pct >= 100 ? '#039855' : pct >= 75 ? '#f79009' : pct >= 40 ? '#fdb022' : '#f04438';

const firstOf = (n) => String(n || '').trim().split(/\s+/)[0];

// One cell of the day summary strip.
function SummaryCell({ chipBg, chipFg, label, value, sub }) {
  return (
    <div style={{ flex: '1 1 150px', minWidth: 150, padding: '12px 16px', borderRight: '1px solid #f2f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, background: chipBg, color: chipFg, flex: 'none' }} />
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#8a949c' }}>{label}</span>
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.4, color: '#1a1a2e', marginTop: 4, ...NUMS }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, fontWeight: 600, color: '#98a2b3', ...NUMS }}>{sub}</div>}
    </div>
  );
}

function formatTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
}

// Convert ISO string to local datetime-local input value (YYYY-MM-DDTHH:MM)
function toLocalInput(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert local datetime-local input to ISO string
function fromLocalInput(localStr) {
  if (!localStr) return null;
  return new Date(localStr).toISOString();
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

// Inclusive list of YYYY-MM-DD strings between two dates. Used to render
// "+ Add" placeholder rows in range view for weeks that predate the clock-in
// feature, so the boss can backfill attendance retroactively.
function datesBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return [];
  const out  = [];
  const cur  = new Date(fromIso + 'T00:00:00');
  const stop = new Date(toIso   + 'T00:00:00');
  while (cur <= stop) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const OC_SERVICES_LIST = ['DAILY OFFICE CLEAN', 'MOP SHOWROOM'];

const navBtn = {
  padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: 4,
  background: '#fff', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

// ── Edit / Create Modal ────────────────────────────────────────────────────
// If `record.id` is set → PATCH (edit existing event).
// If not → POST (create a new event for a contractor who forgot to clock in).
function AttendanceEditModal({ record, date, onSave, onClose }) {
  const isOC = record.user_type === 'office_cleaner';
  const isCreate = !record.id;

  // For create mode: default clock_in to 8:00 AM and clock_out to 5:00 PM on
  // the selected date — the admin just adjusts from there.
  const defaultIn  = isCreate ? `${date}T08:00` : toLocalInput(record.clock_in_at);
  const defaultOut = isCreate
    ? (isOC ? `${date}T10:00` : `${date}T17:00`)
    : toLocalInput(record.clock_out_at);

  const [clockIn,  setClockIn]  = useState(defaultIn);
  const [clockOut, setClockOut] = useState(defaultOut);
  const [services, setServices] = useState(
    isCreate && isOC ? ['DAILY OFFICE CLEAN'] : (record.services || [])
  );
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  function toggleService(svc) {
    setServices(prev =>
      prev.includes(svc) ? prev.filter(s => s !== svc) : [...prev, svc]
    );
  }

  // Recalculate pay from services
  const OC_PRICING = { 'DAILY OFFICE CLEAN': { charged: 174, paid: 100 }, 'MOP SHOWROOM': { charged: 50, paid: 40 } };
  const calcPay = svcs => svcs.reduce((sum, s) => sum + (OC_PRICING[s]?.paid || 0), 0);
  const calcRev = svcs => svcs.reduce((sum, s) => sum + (OC_PRICING[s]?.charged || 0), 0);

  async function handleSave() {
    setSaving(true); setError('');
    try {
      const body = {
        clock_in_at:  clockIn  ? fromLocalInput(clockIn)  : null,
        clock_out_at: clockOut ? fromLocalInput(clockOut) : null,
      };
      if (isOC) {
        body.services        = services;
        body.daily_cost      = calcPay(services);
        body.service_revenue = calcRev(services);
      }
      if (isCreate) {
        // Backend recomputes derived fields — only send inputs.
        await api.createAttendance({
          user_id:      record.user_id,
          date,
          clock_in_at:  body.clock_in_at,
          clock_out_at: body.clock_out_at,
          services:     isOC ? services : undefined,
        });
      } else {
        await api.updateAttendance(record.id, body);
      }
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 0, width: '100%', maxWidth: 440, padding: 24, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1e3a4a' }}>
            {isCreate ? 'Add Attendance' : 'Edit Attendance'} — {record.name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', padding: '8px 12px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 4 }}>Clock In</label>
          <input type="datetime-local" value={clockIn} onChange={e => setClockIn(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 14, boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 4 }}>Clock Out</label>
          <input type="datetime-local" value={clockOut} onChange={e => setClockOut(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 14, boxSizing: 'border-box' }} />
        </div>

        {isOC && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>Services</label>
            {OC_SERVICES_LIST.map(svc => (
              <label key={svc} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}>
                <input type="checkbox" checked={services.includes(svc)} onChange={() => toggleService(svc)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e3a4a' }}>{svc}</span>
                <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
                  ${OC_PRICING[svc]?.paid} pay · ${OC_PRICING[svc]?.charged} charged
                </span>
              </label>
            ))}
            {services.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>
                Total: ${calcPay(services)} pay · ${calcRev(services)} charged
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 0, fontSize: 13, fontWeight: 600, background: '#fff', cursor: 'pointer', color: '#374151' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '10px', border: 'none', borderRadius: 0, fontSize: 13, fontWeight: 700, background: '#1e3a4a', color: '#F5C518', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : (isCreate ? 'Create Entry' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helpers for week/month range derivation
function isoWeekRange(dateStr) {
  // Returns Mon-Sun ISO range containing the given date
  const d = new Date(dateStr + 'T00:00:00');
  const dow = (d.getDay() + 6) % 7; // Mon=0
  const mon = new Date(d); mon.setDate(d.getDate() - dow);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
  return { from: iso(mon), to: iso(sun) };
}
// ISO week number + year for a date
function getISOWeekInfo(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow + 3);
  const year = d.getUTCFullYear();
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const j4Dow = (jan4.getUTCDay() + 6) % 7;
  const firstMon = new Date(jan4);
  firstMon.setUTCDate(jan4.getUTCDate() - j4Dow);
  const week = Math.round((d - firstMon) / 86400000 / 7) + 1;
  return { year, week };
}
// Build a list of the last N ISO weeks ending on the week of `today`.
// Each entry: { key: "2026-W19", anchorDate: "2026-05-04", label: "Week 19 · ..." }
function buildWeekOptions(todayStr, n = 60) {
  const opts = [];
  const seen = new Set();
  let cursor = todayStr;
  while (opts.length < n) {
    const range = isoWeekRange(cursor);
    const info  = getISOWeekInfo(range.from);
    const key   = `${info.year}-W${String(info.week).padStart(2, '0')}`;
    if (!seen.has(key)) {
      seen.add(key);
      const dms = range.from.slice(8, 10) + '/' + range.from.slice(5, 7);
      const dme = range.to.slice(8, 10)   + '/' + range.to.slice(5, 7);
      opts.push({
        key,
        anchorDate: range.from,
        label: `Week ${info.week} · ${dms} – ${dme}/${range.from.slice(0, 4)}`,
      });
    }
    // Step back one week
    const d = new Date(cursor + 'T00:00:00');
    d.setDate(d.getDate() - 7);
    cursor = d.toISOString().slice(0, 10);
  }
  return opts;
}
// Build a list of the last N months ending in the month of `today`.
// Each entry: { key: "2026-05", anchorDate: "2026-05-15", label: "May 2026" }
function buildMonthOptions(todayStr, n = 36) {
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const opts = [];
  const [ty, tm] = todayStr.split('-').map(Number);
  let y = ty, m = tm;
  for (let i = 0; i < n; i++) {
    const key = `${y}-${String(m).padStart(2,'0')}`;
    opts.push({
      key,
      anchorDate: `${key}-15`,
      label: `${monthNames[m - 1]} ${y}`,
    });
    m -= 1;
    if (m < 1) { m = 12; y -= 1; }
  }
  return opts;
}
function monthRange(dateStr) {
  const [y, m] = dateStr.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2,'0')}` };
}
function shiftWeek(dateStr, delta) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}
function shiftMonth(dateStr, delta) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + delta);
  return d.toISOString().slice(0, 10);
}
function fmtRangeShort(from, to) {
  const f = formatDateDisplay(from), t = formatDateDisplay(to);
  return `${f} – ${t}`;
}

export default function AttendancePage() {
  const today = new Date().toISOString().slice(0, 10);
  // 'day' = current behaviour (one day, all contractors with +Add buttons)
  // 'week' / 'month' / 'custom' = ranged view via /attendance/range
  const [viewMode,       setViewMode]       = useState('day');
  const [date,           setDate]           = useState(today);
  const [customFrom,     setCustomFrom]     = useState(today);
  const [customTo,       setCustomTo]       = useState(today);
  const [filterUserId,   setFilterUserId]   = useState('ALL');

  // Day mode → array of contractor rows; Range mode → { events, summary }
  const [attendance,     setAttendance]     = useState([]);
  const [rangeData,      setRangeData]      = useState(null);
  const [contractors,    setContractors]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [editingRecord,  setEditingRecord]  = useState(null);

  // Active range derived from mode + anchor date
  const activeRange = (() => {
    if (viewMode === 'week')   return isoWeekRange(date);
    if (viewMode === 'month')  return monthRange(date);
    if (viewMode === 'custom') return { from: customFrom, to: customTo };
    return { from: date, to: date };
  })();

  // Contractor list — for the user filter dropdown (loaded once)
  useEffect(() => {
    api.getActiveContractors()
      .then(rows => setContractors(rows.filter(c => ['hourly','office_cleaner'].includes(c.type))))
      .catch(() => setContractors([]));
  }, []);

  function loadAttendance() {
    setLoading(true);
    if (viewMode === 'day') {
      api.getAttendance(date)
        .then(data => { setAttendance(Array.isArray(data) ? data : []); setRangeData(null); })
        .catch(() => setAttendance([]))
        .finally(() => setLoading(false));
    } else {
      const { from, to } = activeRange;
      api.getAttendanceRange({ from, to, user_id: filterUserId !== 'ALL' ? filterUserId : undefined })
        .then(data => { setRangeData(data || { events: [], summary: { days: 0, total_cost: 0, total_hours: 0 } }); setAttendance([]); })
        .catch(() => setRangeData({ events: [], summary: { days: 0, total_cost: 0, total_hours: 0 } }))
        .finally(() => setLoading(false));
    }
  }

  useEffect(() => {
    loadAttendance();
  // eslint-disable-next-line
  }, [viewMode, date, customFrom, customTo, filterUserId]);

  async function handleDelete(record) {
    if (!record.id) return alert('No clock event to delete for this contractor.');
    if (!window.confirm(`Delete attendance record for ${record.name} on ${formatDateDisplay(date)}?`)) return;
    try {
      await api.deleteAttendance(record.id);
      loadAttendance();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  }

  // ── "Adjust" / "½ Day" — snap random clock times to a clean shift ────────
  // Hourly contractors clock in/out at scattered times. handleAdjust snaps
  // the row to 08:00 → endTime: '16:00' for a full 8.00h day, '12:00' for a
  // 4.00h half day (workers like Fellipe / Rafael often do half days).
  // Backend recomputes hours + cost. One-click, no confirmation — a
  // misclick is recoverable via ✎ Edit.
  // Office cleaners are paid per service, not by hours, so they get a single
  // "Adjust 2.5h" snap (their standard shift) instead of full/half day. When
  // there's no record yet we CREATE one — the admin can attach the service
  // afterwards via ✎; this just logs the 2.5h.
  const [adjustingId, setAdjustingId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);   // Site chip / row detail
  const [rowMenu, setRowMenu] = useState(null);           // per-row actions menu
  async function handleAdjust(record, endTime = '16:00', startTime = '08:00') {
    // In range view the row carries its own date; in day view fall back to
    // the page's selected date.
    const recDate = record.date || date;
    const busyKey = record.id || `new:${record.user_id}`;
    setAdjustingId(busyKey);
    try {
      const clock_in_at  = fromLocalInput(`${recDate}T${startTime}`);
      const clock_out_at = fromLocalInput(`${recDate}T${endTime}`);
      if (record.id) {
        await api.updateAttendance(record.id, { clock_in_at, clock_out_at });
      } else {
        await api.createAttendance({
          user_id:  record.user_id,
          date:     recDate,
          clock_in_at,
          clock_out_at,
          services: record.user_type === 'office_cleaner' ? [] : undefined,
        });
      }
      loadAttendance();
    } catch (err) {
      alert(err.message || 'Failed to adjust');
    } finally {
      setAdjustingId(null);
    }
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>

      {/* Header — title, mode chips, date controls, contractor filter */}
      <div style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1e3a4a', textTransform: 'uppercase', letterSpacing: 1 }}>
            Attendance
          </h2>
          {/* Mode chips */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[['day','Day'],['week','Week'],['month','Month'],['custom','Custom']].map(([v,l]) => (
              <button key={v} onClick={() => setViewMode(v)} style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                border: viewMode === v ? '2px solid #1e3a4a' : '1px solid #e5e7eb',
                background: viewMode === v ? '#1e3a4a' : '#fff',
                color: viewMode === v ? '#fff' : '#475569',
                cursor: 'pointer',
              }}>{l}</button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          {/* Contractor filter — visible in all modes; in Day mode it's purely cosmetic
              since the daily endpoint always returns one row per active contractor */}
          <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Contractor:
          </label>
          <select
            value={filterUserId}
            onChange={e => setFilterUserId(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
          >
            <option value="ALL">All</option>
            {contractors.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Date / range controls — change shape per mode */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {viewMode === 'day' && (
            <>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13, color: '#374151' }}
              />
              {date !== today && (
                <button onClick={() => setDate(today)} style={{
                  padding: '5px 12px', border: '1px solid #4A7A91', borderRadius: 0,
                  fontSize: 12, fontWeight: 600, color: '#4A7A91', background: '#e8f2f7', cursor: 'pointer',
                }}>Today</button>
              )}
              <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
                {formatDateDisplay(date)}
              </span>
            </>
          )}

          {viewMode === 'week' && (() => {
            // Compute the current ISO week key so the dropdown highlights it.
            const info = getISOWeekInfo(date);
            const currentKey = `${info.year}-W${String(info.week).padStart(2, '0')}`;
            // Last 60 weeks anchored on today (so the dropdown always lists
            // recent + future-this-quarter weeks first).
            const weekOpts = buildWeekOptions(today, 60);
            // If the user navigated to an older week not in the last-60 list,
            // make sure it still appears so the select is never out-of-sync.
            if (!weekOpts.find(w => w.key === currentKey)) {
              const range = isoWeekRange(date);
              const dms = range.from.slice(8, 10) + '/' + range.from.slice(5, 7);
              const dme = range.to.slice(8, 10)   + '/' + range.to.slice(5, 7);
              weekOpts.unshift({
                key: currentKey,
                anchorDate: range.from,
                label: `Week ${info.week} · ${dms} – ${dme}/${range.from.slice(0, 4)}`,
              });
            }
            return (
              <>
                <button onClick={() => setDate(shiftWeek(date, -1))} style={navBtn}>← Prev</button>
                <select
                  value={currentKey}
                  onChange={e => {
                    const opt = weekOpts.find(w => w.key === e.target.value);
                    if (opt) setDate(opt.anchorDate);
                  }}
                  style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, minWidth: 240, fontWeight: 600 }}
                >
                  {weekOpts.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
                </select>
                <button onClick={() => setDate(shiftWeek(date, +1))} style={navBtn}>Next →</button>
                <button onClick={() => setDate(today)} style={navBtn}>This week</button>
              </>
            );
          })()}

          {viewMode === 'month' && (() => {
            const currentKey = date.slice(0, 7);
            const monthOpts = buildMonthOptions(today, 36);
            if (!monthOpts.find(m => m.key === currentKey)) {
              const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const [y, m] = currentKey.split('-').map(Number);
              monthOpts.unshift({
                key: currentKey,
                anchorDate: `${currentKey}-15`,
                label: `${monthNames[m - 1]} ${y}`,
              });
            }
            return (
              <>
                <button onClick={() => setDate(shiftMonth(date, -1))} style={navBtn}>← Prev</button>
                <select
                  value={currentKey}
                  onChange={e => {
                    const opt = monthOpts.find(x => x.key === e.target.value);
                    if (opt) setDate(opt.anchorDate);
                  }}
                  style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, minWidth: 180, fontWeight: 600 }}
                >
                  {monthOpts.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
                <button onClick={() => setDate(shiftMonth(date, +1))} style={navBtn}>Next →</button>
                <button onClick={() => setDate(today)} style={navBtn}>This month</button>
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
                  {fmtRangeShort(activeRange.from, activeRange.to)}
                </span>
              </>
            );
          })()}

          {viewMode === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13 }} />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13 }} />
            </>
          )}
        </div>

        {/* Range summary card */}
        {viewMode !== 'day' && rangeData && (
          <div style={{
            display: 'flex', gap: 24, padding: '10px 14px', borderRadius: 8,
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            fontSize: 13, color: '#15803d', fontWeight: 600,
          }}>
            <span>{rangeData.summary.days} day{rangeData.summary.days === 1 ? '' : 's'}</span>
            <span>·</span>
            <span>${rangeData.summary.total_cost.toFixed(2)} total cost</span>
            <span>·</span>
            <span>{fmtHM(rangeData.summary.total_hours)} total</span>
            {filterUserId !== 'ALL' && (
              <>
                <span>·</span>
                <span>👤 {contractors.find(c => String(c.id) === filterUserId)?.name || 'contractor'}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Attendance table — rows differ per mode */}
      {(() => {
        // Pick the row source: range mode uses rangeData.events, day mode uses
        // the per-contractor list (which includes empty rows for "+ Add").
        const isRange = viewMode !== 'day';
        const rows = isRange ? (rangeData?.events || []) : attendance;

        // Decide what rows to render. Three cases:
        //   1. Range view + specific contractor → synthesise a row per date
        //      in the range. Real events fill in their date; missing dates
        //      get phantom rows (id: null) so the existing render path
        //      shows a "+ Add" button — perfect for weeks predating the
        //      clock-in feature where the boss has to log retroactively.
        //   2. Day view + specific contractor → filter the backend's
        //      all-contractors list down to the one selected.
        //   3. Otherwise → show whatever the backend returned.
        let visibleRows;
        if (isRange && filterUserId !== 'ALL') {
          const recordsByDate = {};
          rows.forEach(r => { if (r.date) recordsByDate[r.date] = r; });
          const contractor = contractors.find(c => String(c.id) === filterUserId);
          const allDates   = datesBetween(activeRange.from, activeRange.to);
          visibleRows = allDates.map(d => recordsByDate[d] || {
            date:      d,
            user_id:   parseInt(filterUserId),
            name:      contractor?.name || 'contractor',
            user_type: contractor?.type || 'hourly',
            id:        null,
          });
        } else if (!isRange && filterUserId !== 'ALL') {
          visibleRows = rows.filter(a => String(a.user_id) === filterUserId);
        } else {
          visibleRows = rows;
        }

        // Totals for the black footer bar — always from the rows on screen.
        const dayTotals = visibleRows.reduce((t, r) => ({
          onClock:  t.onClock + (r.clock_in_at ? 1 : 0),
          hours:    t.hours + (Number(r.hours_worked) || 0),
          cost:     t.cost + (Number(r.daily_cost) || 0),
          produced: t.produced + (Number(r.produced_value) || 0),
        }), { onClock: 0, hours: 0, cost: 0, produced: 0 });

        // Day summary — every figure derived from the rows on screen, never
        // hard-coded, so it can't disagree with the table under it.
        const onShift    = visibleRows.filter(r => r.clock_in_at && !r.clock_out_at);
        const completed  = visibleRows.filter(r => r.clock_in_at && r.clock_out_at);
        const notIn      = visibleRows.filter(r => !r.clock_in_at);
        const offSite    = visibleRows.filter(r => {
          const i = suburbOf(r.address_in), o = suburbOf(r.address_out);
          return i && o && i !== o;
        });
        const dayCoverage = dayTotals.cost > 0 ? (dayTotals.produced / dayTotals.cost) * 100 : null;
        const attention = [
          ...notIn.map(r => `${firstOf(r.name)} not clocked in`),
          ...offSite.map(r => `${firstOf(r.name)} clocked out off-site`),
        ];

        const emptyMessage = isRange
          ? `No clock-in records in this range${filterUserId !== 'ALL' ? ' for the selected contractor' : ''}.`
          : `No clock-in records for ${formatDateDisplay(date)}.`;

        return (
      <>
      {!loading && !isRange && visibleRows.length > 0 && (
        <div className="card" style={{ marginBottom: 12, padding: 0, display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
          <SummaryCell chipBg="#e8f2f7" chipFg="#1e3a4a" label="On shift now"
            value={onShift.length} sub={onShift.length ? onShift.map(r => firstOf(r.name)).join(' · ') : 'all clocked out'} />
          <SummaryCell chipBg="#d1fadf" chipFg="#039855" label="Completed"
            value={completed.length} sub={`of ${visibleRows.length} rostered`} />
          <SummaryCell chipBg="#fef0c7" chipFg="#b54708" label="Not clocked in"
            value={notIn.length} sub={notIn.length ? notIn.map(r => firstOf(r.name)).join(' · ') : 'everyone in'} />
          <SummaryCell chipBg="#ede9fe" chipFg="#6d28d9" label="Hours today"
            value={fmtHM(dayTotals.hours)} sub={`${completed.length + onShift.length} on the clock`} />
          <SummaryCell chipBg="#fef3c7" chipFg="#a16207" label="Day cost"
            value={`$${dayTotals.cost.toFixed(2)}`}
            sub={`$${dayTotals.produced.toFixed(0)} produced${dayCoverage != null ? ` · ${Math.round(dayCoverage)}%` : ''}`} />
          {attention.length > 0 && (
            <div style={{ margin: '10px 14px', padding: '10px 13px', background: '#fef0c7', color: '#b54708', borderRadius: 10, alignSelf: 'center', maxWidth: 320 }}>
              <div style={{ fontSize: 12, fontWeight: 800 }}>{attention.length} {attention.length === 1 ? 'entry needs' : 'entries need'} a look</div>
              <div style={{ fontSize: 11, marginTop: 2 }}>{attention.join(' · ')}</div>
            </div>
          )}
        </div>
      )}
      <div className="card" style={{ marginBottom: 24, borderTop: '3px solid #F5C518', padding: 0 }}>
        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            Loading attendance…
          </div>
        ) : visibleRows.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
            {emptyMessage}
          </div>
        ) : (
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#f7f9fa', borderBottom: '1px solid #eef0f2' }}>
                  {(isRange
                    ? ['Date', 'Contractor', 'Clock in', 'Clock out', 'Site', 'Hours', 'Rate', 'Cost', 'Produced today', '']
                    : ['Contractor', 'Clock in', 'Clock out', 'Site', 'Hours', 'Rate', 'Cost', 'Produced today', '']
                  ).map((h, hi) => (
                    <th key={h + hi} style={{
                      padding: '9px 12px', textAlign: ['Hours', 'Rate', 'Cost'].includes(h) ? 'right' : 'left',
                      fontWeight: 800, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1.2,
                      color: '#667085', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((a, i) => {
                  const isOC = a.user_type === 'office_cleaner';
                  const eng = isOC ? ENGAGEMENT.office : ENGAGEMENT.hourly;
                  const expected = isOC ? 2.5 : 8;
                  const cost = a.daily_cost;
                  // Derive the hourly rate from what was actually paid for the
                  // hours worked — daily_wage means an hourly rate for hourly
                  // contractors but a day fee for the office cleaner, so
                  // dividing it by expected hours gave nonsense ($3.56/h).
                  const rate = (cost != null && a.hours_worked > 0)
                    ? cost / a.hours_worked
                    : (!isOC && a.daily_wage ? a.daily_wage : null);
                  const rateEst = !(cost != null && a.hours_worked > 0);
                  const produced = Number(a.produced_value) || 0;
                  const coverage = cost > 0 ? (produced / cost) * 100 : null;
                  const inSub  = suburbOf(a.address_in);
                  const outSub = suburbOf(a.address_out);
                  const moved  = inSub && outSub && inSub !== outSub;
                  const expandKey = a.id || `u${a.user_id}-${a.date || date}`;
                  const open = expandedRow === expandKey;
                  return (
                    <React.Fragment key={expandKey}>
                    <tr className="att-row" style={{ borderBottom: '1px solid #f2f4f6', background: '#fff' }}>
                      {isRange && (
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1e3a4a', whiteSpace: 'nowrap', ...NUMS }}>
                          {formatDateDisplay(a.date)}
                        </td>
                      )}
                      {/* Contractor — avatar + engagement tag, so the old Type column goes away */}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <span style={{
                            width: 30, height: 30, borderRadius: '50%', flex: 'none',
                            background: eng.bg, color: eng.fg, fontSize: 11, fontWeight: 800,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>{initialsOf(a.name)}</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap' }}>{a.name}</div>
                            <span style={{ fontSize: 9.5, fontWeight: 700, color: eng.fg, background: eng.bg, padding: '1px 6px', borderRadius: 5 }}>{eng.label}</span>
                          </div>
                        </div>
                      </td>

                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {a.clock_in_at
                          ? <><div style={{ fontSize: 13, fontWeight: 800, ...NUMS }}>{formatTime(a.clock_in_at)}</div>
                              <div style={{ fontSize: 10, color: '#98a2b3' }}>{a.hours_worked == null ? 'on shift' : a.hours_worked >= expected ? 'full day' : 'short day'}</div></>
                          : <><div style={{ fontWeight: 700, color: '#b54708' }}>Not clocked in</div>
                              <div style={{ fontSize: 10, color: '#98a2b3' }}>expected 08:00 am</div></>}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {a.clock_out_at
                          ? <div style={{ fontSize: 13, fontWeight: 800, ...NUMS }}>{formatTime(a.clock_out_at)}</div>
                          : <span style={{ color: '#dfe3e7', fontSize: 15 }}>—</span>}
                      </td>

                      {/* Site — one chip replaces both address columns; amber when
                          they clocked out somewhere else. Click expands the row. */}
                      <td style={{ padding: '10px 12px' }}>
                        {(inSub || outSub) ? (
                          <span onClick={() => setExpandedRow(open ? null : expandKey)} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                            background: moved ? '#fef0c7' : '#f2f4f6', color: moved ? '#b54708' : '#475467',
                            fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6, whiteSpace: 'nowrap',
                          }}>{moved ? `${inSub} → ${outSub}` : (inSub || outSub)}</span>
                        ) : <span style={{ color: '#c3ccd3', fontSize: 11 }}>No GPS</span>}
                      </td>

                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 800, color: a.hours_worked != null ? '#1a1a2e' : '#c3ccd3', ...NUMS }}>
                        {a.hours_worked != null ? fmtHM(a.hours_worked) : '—'}
                      </td>

                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {rate != null
                          ? <><div style={{ fontSize: 12.5, fontWeight: 800, ...NUMS }}>${rate.toFixed(2)}</div>
                              <div style={{ fontSize: 9.5, color: '#98a2b3' }}>per hour{rateEst ? ' (est.)' : ''}</div></>
                          : <span style={{ color: '#c3ccd3' }}>—</span>}
                      </td>

                      {/* Cost is payroll, not an error — never red. */}
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 800, color: cost != null ? '#1a1a2e' : '#c3ccd3', ...NUMS }}>
                        {cost != null ? `$${cost.toFixed(2)}` : '—'}
                      </td>

                      {/* Produced today — job value against this person's cost */}
                      <td style={{ padding: '10px 12px', minWidth: 168 }}>
                        {isOC ? (
                          <span style={{ fontSize: 11, color: '#98a2b3' }}>Contract — no job value</span>
                        ) : (
                          <>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              <span style={{ fontSize: 12.5, fontWeight: 800, ...NUMS }}>${produced.toFixed(2)}</span>
                              <span style={{ fontSize: 10, color: '#98a2b3' }}>{a.job_count || 0} {a.job_count === 1 ? 'job' : 'jobs'}</span>
                              <span style={{ flex: 1 }} />
                              {coverage != null && (
                                <span style={{ fontSize: 11, fontWeight: 800, color: coverBand(coverage), ...NUMS }}>{Math.round(coverage)}%</span>
                              )}
                            </div>
                            <div style={{ position: 'relative', height: 6, background: '#f2f4f6', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(coverage || 0, 100)}%`, background: coverBand(coverage || 0), borderRadius: 3 }} />
                              <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 1.5, background: '#1a1a2e' }} />
                            </div>
                          </>
                        )}
                      </td>

                      {/* One contextual action; the rest lives in the menu, so
                          delete is no longer one mis-tap away on every row. */}
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                          {a.id ? (
                            <button onClick={() => handleAdjust(a, isOC ? '10:30' : '16:00')} disabled={adjustingId === a.id}
                              style={{ padding: '5px 12px', fontSize: 11.5, fontWeight: 700, border: '1px solid #e4e7ec', background: '#fff', color: '#475467', cursor: adjustingId === a.id ? 'wait' : 'pointer', borderRadius: 8 }}>
                              {adjustingId === a.id ? '…' : 'Adjust'}
                            </button>
                          ) : (
                            <button onClick={() => setEditingRecord(a)}
                              style={{ padding: '5px 12px', fontSize: 11.5, fontWeight: 700, border: 'none', background: '#4A7A91', color: '#fff', cursor: 'pointer', borderRadius: 8 }}>
                              + Add entry
                            </button>
                          )}
                          <button onClick={() => setRowMenu(rowMenu === expandKey ? null : expandKey)}
                            style={{ padding: '2px 6px', fontSize: 15, lineHeight: 1, border: 'none', background: 'none', color: '#c3ccd3', cursor: 'pointer' }}>⋯</button>
                        </div>
                        {rowMenu === expandKey && (
                          <>
                            <div onClick={() => setRowMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                            <div style={{ position: 'absolute', right: 8, top: '100%', zIndex: 31, minWidth: 150, background: '#fff', border: '1px solid #e5e9ec', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4 }}>
                              {a.id && !isOC && (
                                <button onClick={() => { setRowMenu(null); handleAdjust(a, '12:00'); }} style={attMenuItem('#b54708')}>Half day</button>
                              )}
                              <button onClick={() => { setRowMenu(null); setEditingRecord(a); }} style={attMenuItem('#475467')}>{a.id ? 'Edit' : 'Add entry'}</button>
                              <button onClick={() => { setRowMenu(null); setExpandedRow(open ? null : expandKey); }} style={attMenuItem('#475467')}>{open ? 'Hide detail' : 'Show detail'}</button>
                              {a.id && (
                                <button onClick={() => { setRowMenu(null); handleDelete(a); }} style={attMenuItem('#c53030')}>Delete</button>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>

                    {open && (
                      <tr>
                        <td colSpan={isRange ? 10 : 9} style={{ background: '#fbfcfd', padding: '13px 16px 13px 60px', borderBottom: '1px solid #f2f4f6' }}>
                          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', fontSize: 11.5 }}>
                            <div><div style={attLbl}>Clocked in at</div><div style={{ color: '#475467' }}>{a.address_in || '—'}</div></div>
                            <div><div style={attLbl}>Clocked out at</div><div style={{ color: '#475467' }}>{a.address_out || '—'}</div></div>
                            <div><div style={attLbl}>Checklist</div><div style={{ color: '#475467' }}>{(a.checklist?.length || 0)} task{a.checklist?.length === 1 ? '' : 's'} done</div></div>
                            {a.services?.length > 0 && (
                              <div><div style={attLbl}>Services</div><div style={{ color: '#475467' }}>{a.services.join(' · ')}</div></div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {/* Totals — the old screen never added the day up */}
              <tfoot>
                <tr style={{ background: '#0a0a0a', color: '#fff' }}>
                  <td colSpan={isRange ? 6 : 5} style={{ padding: '12px 16px', fontSize: 12, fontWeight: 800 }}>
                    {dayTotals.onClock} on the clock
                  </td>
                  <td colSpan={2} style={{ padding: '12px 8px', textAlign: 'right', fontSize: 12, fontWeight: 800, ...NUMS }}>
                    Hours {fmtHM(dayTotals.hours)}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: 12, fontWeight: 800, ...NUMS }}>
                    <span style={{ color: '#98a2b3', fontWeight: 700 }}>Produced </span>${dayTotals.produced.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <span style={{ display: 'inline-block', background: '#FFD000', color: '#0a0a0a', borderRadius: 8, padding: '5px 12px', fontSize: 12.5, fontWeight: 800, ...NUMS }}>
                      ${dayTotals.cost.toFixed(2)}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      </>
        );
      })()}

      {/* Edit / create modal */}
      {editingRecord && (
        <AttendanceEditModal
          record={editingRecord}
          // Phantom rows in range view carry their own date — prefer it
          // over the page's `date` state so "+ Add" on a Week 17 row opens
          // the modal with the right day prefilled, not today's date.
          date={editingRecord.date || date}
          onSave={() => { setEditingRecord(null); loadAttendance(); }}
          onClose={() => setEditingRecord(null)}
        />
      )}

    </div>
  );
}
